'use client'

// libraries
import clsx from 'clsx'
import { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectFade, Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import StudyCard, { StudyCardProps } from '/react4xp/components/StudyCard'
import Accordion, { AccordionList } from '/react4xp/components/Accordion'
import RichText from '/react4xp/components/RichText'

// types
export interface StudyProgressDetails {
    year?: number
    semester?: number
    cards?: StudyCardProps[]
    accordion?: StudyProgressAccordion[]
    totalPoints?: number
}

export interface StudyProgressAccordion {
    title?: string
    content?: {
        cards?: StudyCardProps[]
        description?: string
    }
}

export interface StudyProgressSingleStrings {
    year?: string
    semester?: string
    points?: string
    pointsAbbreviation?: string
    total?: string
}

export interface StudyProgressProps {
    className?: string
    title?: string
    details?: StudyProgressDetails[]
    singleStrings?: StudyProgressSingleStrings
}

// css
import './index.scss'

export default function StudyProgress({
    className,
    title,
    details = [],
    singleStrings
}: StudyProgressProps) {

    const swiperRef = useRef<SwiperType | null>(null)

    // callback to update swiper height when accordion toggles
    const handleAccordionToggle = () => {
        setTimeout(() => {
            if (swiperRef.current) {
                swiperRef.current.updateAutoHeight()
            }
        }, 50)
    }

    // transform single item to new format
    const transformItem = (item: StudyProgressDetails): StudyProgressDetails => ({
        year: item.year,
        semester: item.semester,
        cards: item.cards,
        accordion: item.accordion,
        totalPoints: item.totalPoints
    })

    // calculate points from cards
    const getCardPoints = (cards: StudyCardProps[] = []) => 
        cards.reduce((sum, card) => sum + (card.points || 0), 0)

    // calculate points from accordion
    const getAccordionPoints = (accordion: StudyProgressAccordion[] = []) =>
        accordion.reduce((sum, acc) => sum + getCardPoints(acc.content?.cards), 0)

    // calculate total points for an item
    const getItemPoints = (item: StudyProgressDetails) => 
        getCardPoints(item?.cards) + getAccordionPoints(item?.accordion)

    // transform all items
    const transformedItems = details.map(transformItem)

    // group by year, then by semester
    const groupedByYear = transformedItems.reduce((acc, item) => {
        const year = String(item.year)
        const semester = String(item.semester)
        
        if (!acc[year]) acc[year] = {}
        if (!acc[year][semester]) acc[year][semester] = []
        
        acc[year][semester].push(item)
        return acc
    }, {} as Record<string, Record<string, StudyProgressDetails[]>>)

    // convert to final format with totals
    const itemsGroupedByYear = Object.entries(groupedByYear)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([year, semesters]) => {
            const allItems = Object.values(semesters).flat()
            const customTotalPoints = transformedItems.find(item => item.year?.toString() == year)?.totalPoints
            const totalPoints = customTotalPoints || allItems.reduce((sum, item) => sum + getItemPoints(item), 0)
            
            return {
                year,
                totalPoints,
                semesters: Object.entries(semesters)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([semester, items]) => ({ semester, items }))
            }
        })

    return (
        <Section
            className={clsx(
                'study-progress-component',
                className
            )}
        >
            <div className='container'>
                <RoundedBox
                    bgColor='beige'
                    className='study-progress-component__box'
                >
                    <div className='row'>
                        <div className='col-lg-10 offset-lg-1'>

                            {title && (
                                <h2 className='h2 study-progress-component__title'>
                                    {title}
                                </h2>
                            )}

                            <div className='study-progress-component__details'>

                                <div className='study-progress-component__pagination' />

                                <Swiper
                                    modules={[EffectFade, Pagination]}
                                    className='study-progress-component__slider'
                                    spaceBetween={0}
                                    slidesPerView={1}
                                    autoHeight
                                    allowTouchMove={false}
                                    effect='fade'
                                    fadeEffect={{ crossFade: true }}
                                    onSwiper={(swiper) => {
                                        swiperRef.current = swiper
                                    }}
                                    pagination={{
                                        clickable: true,
                                        el: '.study-progress-component__pagination',
                                        renderBullet: (i, className) => {
                                            return `
                                                <button class='${className} hover-underline h4'>
                                                    ${singleStrings?.year} ${i + 1}
                                                </button>
                                            `
                                        }
                                    }}
                                >
                                    {itemsGroupedByYear.map((item, i) => (
                                        <SwiperSlide key={i}>
                                            
                                            {item.semesters.map((item2, i2) => (
                                                <div key={i2} className='semester-block'>
                                                    
                                                    <h3 className='h4 semester-block__title'>
                                                        {i2 + 1}. {singleStrings?.semester}
                                                    </h3>

                                                    {item2.items.map((item3, i3) => (
                                                        <div key={i3} className='semester-block__items'>

                                                            {item3.cards && (
                                                                <div className='semester-block__items__cards'>
                                                                    {item3.cards.map((item4, i4) => (
                                                                        <StudyCard
                                                                            key={i4}
                                                                            href={item4.href}
                                                                            title={item4.title}
                                                                            subject={item4.subject}
                                                                            points={item4.points}
                                                                            pointsText={item4.pointsText}
                                                                            pointsAbbreviation={singleStrings?.pointsAbbreviation}
                                                                            studyCode={item4.studyCode}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {item3.accordion && (
                                                                <AccordionList oneAtTime>
                                                                    {item3.accordion.map((item4, i4) => (
                                                                        <Accordion
                                                                            key={i4}
                                                                            title={item4.title}
                                                                            color='green'
                                                                            onToggle={handleAccordionToggle}
                                                                            content={
                                                                                <div className='semester-block__accordion-content'>
                                                                                    
                                                                                    {item4.content?.cards && (
                                                                                        <div className='semester-block__items__cards'>
                                                                                            {item4.content.cards.map((item5, i5) => (
                                                                                                <StudyCard
                                                                                                    key={i5}
                                                                                                    href={item5.href}
                                                                                                    title={item5.title}
                                                                                                    subject={item5.subject}
                                                                                                    points={item5.points}
                                                                                                    pointsText={item5.pointsText}
                                                                                                    pointsAbbreviation={singleStrings?.pointsAbbreviation}
                                                                                                    studyCode={item5.studyCode}
                                                                                                />
                                                                                            ))}
                                                                                        </div>
                                                                                    )}

                                                                                    {item4.content?.description && (
                                                                                        <RichText className='semester-block__accordion-content__description'>
                                                                                            {item4.content.description}
                                                                                        </RichText>
                                                                                    )}

                                                                                </div>
                                                                            }
                                                                        />
                                                                    ))}
                                                                </AccordionList>
                                                            )}

                                                        </div>
                                                    ))}

                                                </div>
                                            ))}

                                            {item.totalPoints > 0 && (
                                                <h3 className='body year-total'>
                                                    {singleStrings?.total} {item.totalPoints} {singleStrings?.points}
                                                </h3>
                                            )}

                                        </SwiperSlide>
                                    ))}
                                </Swiper>

                            </div>

                        </div>
                    </div>
                </RoundedBox>
            </div>
        </Section>
    )
}